import { useCallback, useRef, useState } from 'react';

import api, { FetchError, FetchRequest } from '../../common/api/api';
import { apiResolve } from '../../common/api/apiResolve';
import { removeUndefinedProps } from '../../common/utils/object.utils';
import {
  TestRailApiProjectPaginatedList,
  TestRailApiSectionPaginatedList,
  TestRailApiSuite,
  TestRailApiTestCasePaginatedList
} from './TestRail.interface';

export interface GetTestRailApiProjectsParams {
  limit?: number;
  offset?: number;
  isCompleted?: boolean;
}

export function useGetTestRailApiProjects() {
  // similar to useApiFetch hooks but expose the state setter
  // to have more control (e.g. clear error manually)
  const [state, setState] = useState<{
    projectList?: TestRailApiProjectPaginatedList;
    isLoading: boolean;
    error?: FetchError;
  }>({ isLoading: false });

  const getProjects = useCallback(
    async (params: GetTestRailApiProjectsParams = {}) => {
      setState(state => ({ ...state, isLoading: true, error: undefined }));

      const request = api.get(
        'testrail/api/projects',
        removeUndefinedProps(params)
      );
      const result = apiResolve<TestRailApiProjectPaginatedList>(request);
      const { data, error } = await result;

      setState({ projectList: data, isLoading: false, error });

      return result;
    },
    []
  );

  return { ...state, getProjects, setState };
}

export function useGetTestRailApiSuites() {
  // preserve the request for aborting
  const requestRef = useRef<FetchRequest>();

  // similar to useApiFetch hooks but expose the state setter
  // to have more control (e.g. clear error manually)
  const [state, setState] = useState<{
    suites?: TestRailApiSuite[];
    isLoading: boolean;
    error?: FetchError;
  }>({ isLoading: false });

  const abortGetSuites = useCallback(() => {
    requestRef.current?.abort();
    requestRef.current = undefined;
  }, []);

  const getSuites = useCallback(
    async (testRailProjectId: number) => {
      abortGetSuites();
      setState(state => ({ ...state, isLoading: true, error: undefined }));

      const request = api.get('testrail/api/suites', {
        projectId: testRailProjectId
      });
      requestRef.current = request;
      const result = apiResolve<TestRailApiSuite[]>(request);
      const { data, error } = await result;

      // remove request when done
      requestRef.current = undefined;
      setState({ suites: data, isLoading: false, error });

      return result;
    },
    [abortGetSuites]
  );

  return { ...state, getSuites, abortGetSuites, setState };
}

export function useGetTestRailApiSections() {
  // preserve the request for aborting
  const requestRef = useRef<FetchRequest>();

  // similar to useApiFetch hooks but expose the state setter
  // to have more control (e.g. clear error manually)
  const [state, setState] = useState<{
    sectionList?: TestRailApiSectionPaginatedList;
    isLoading: boolean;
    error?: FetchError;
  }>({ isLoading: false });

  const abortGetSections = useCallback(() => {
    requestRef.current?.abort();
    requestRef.current = undefined;
  }, []);

  const getSections = useCallback(
    async (options: { testRailProjectId: number; testRailSuiteId: number }) => {
      abortGetSections();
      setState(state => ({ ...state, isLoading: true, error: undefined }));

      const request = api.get('testrail/api/sections', {
        projectId: options.testRailProjectId,
        suiteId: options.testRailSuiteId
      });
      requestRef.current = request;
      const result = apiResolve<TestRailApiSectionPaginatedList>(request);
      const { data, error } = await result;

      // remove request when done
      requestRef.current = undefined;
      setState({ sectionList: data, isLoading: false, error });

      return result;
    },
    [abortGetSections]
  );

  return { ...state, getSections, abortGetSections, setState };
}

export interface GetTestRailApiTestCasesParams {
  projectId: number;
  suiteId?: number;
  limit?: number;
  offset?: number;
  filter?: string;
}

export function useGetTestRailApiTestCases() {
  // preserve the request for aborting
  const requestRef = useRef<FetchRequest>();

  // similar to useApiFetch hooks but expose the state setter
  // to have more control (e.g. clear error manually)
  const [state, setState] = useState<{
    testCaseList?: TestRailApiTestCasePaginatedList;
    isLoading: boolean;
    error?: FetchError;
  }>({ isLoading: false });

  const abortGetTestCases = useCallback(() => {
    requestRef.current?.abort();
    requestRef.current = undefined;
  }, []);

  const getTestCases = useCallback(
    async (params: GetTestRailApiTestCasesParams) => {
      abortGetTestCases();
      setState(state => ({ ...state, isLoading: true, error: undefined }));

      const request = api.get(
        'testrail/api/cases',
        removeUndefinedProps(params)
      );
      requestRef.current = request;
      const result = apiResolve<TestRailApiTestCasePaginatedList>(request);
      const { data, error } = await result;

      // remove request when done
      requestRef.current = undefined;
      setState({ testCaseList: data, isLoading: false, error });

      return result;
    },
    [abortGetTestCases]
  );

  return { ...state, getTestCases, abortGetTestCases, setState };
}
